import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, Clock, MapPin, Filter, ChevronDown, ChevronUp, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { searchFlight } from '../../../../../services/flight-tracking/flightService';
import FlightTrackingResults from './FlightTrackingResults';
import TableSkeleton from '../../../../../components/feedback/TableSkeleton';
import EmptyStateTable from '../../../../../components/feedback/EmptyStateTable';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import { getCachedVouchers, peekCachedVouchers } from '../../../../../services/vouchers/vouchersCache';
import {
  flightColumns,
  flightGhostRows,
  parseAnyDate,
  getTimelineBadge,
  formatBookingId
} from '../../../utils/trackingHelpers';
import { DAYS_FILTER_OPTIONS } from '../../../../../data/tracking/trackingData';

const UpcomingFlightsTable = () => {
  const navigate = useNavigate();
  const [flightVouchers, setFlightVouchers] = useState([]);
  const [loading, setLoading] = useState(!peekCachedVouchers());
  const [daysFilter, setDaysFilter] = useState(3);
  const [expandedRow, setExpandedRow] = useState(null);
  const [rowDetailData, setRowDetailData] = useState({});
  const [fetchingRow, setFetchingRow] = useState(null);
  const [errorRow, setErrorRow] = useState(null);

  useEffect(() => {
    const processVouchers = (vouchers) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const filterDateLimit = new Date();
      filterDateLimit.setDate(today.getDate() + daysFilter);
      filterDateLimit.setHours(23, 59, 59, 999);

      const upcomingFlights = [];

      (vouchers || []).forEach((voucher) => {
        const flightBookings =
          voucher.travel_bookings?.filter(
            (tb) => tb.mode_of_transport?.toLowerCase() === 'flight' || tb.mode_of_transport === 'Flight'
          ) || [];

        if (flightBookings.length === 0) return;

        const startDateStr = voucher.tour_info?.start_date;

        flightBookings.forEach((flight) => {
          let dateToUse = flight.journey_date;
          if (!dateToUse || dateToUse === 'N/A') {
            dateToUse = startDateStr;
          }

          if (dateToUse && dateToUse !== 'N/A') {
            const start = parseAnyDate(dateToUse);

            if (start && start >= today && start <= filterDateLimit) {
              const rawFlightNum =
                flight.vehicle_number || flight.flight_number || flight.flight_train_number || 'N/A';
              const flightOp = flight.operator || flight.airline || 'Flight';

              upcomingFlights.push({
                guestName: voucher.guest_information?.guest_name || 'N/A',
                bookingId: voucher.booking_id,
                startDate: startDateStr || 'N/A',
                flightNumber: rawFlightNum,
                operator: flightOp,
                pnr: flight.pnr || flight.pnr_number || 'N/A',
                from: flight.from || flight.from_city || 'N/A',
                to: flight.to || flight.to_city || 'N/A',
                departureTime: flight.departure_time || 'N/A',
                journeyDate: dateToUse,
                fullVoucher: voucher
              });
            }
          }
        });
      });

      setFlightVouchers(upcomingFlights);
    };

    const loadVouchers = async () => {
      const cached = peekCachedVouchers();
      if (cached) {
        processVouchers(cached);
        setLoading(false);
      } else {
        setLoading(true);
      }

      try {
        const vouchers = await getCachedVouchers();
        processVouchers(vouchers);
      } catch (err) {
        console.error('Failed to fetch vouchers for flights', err);
      } finally {
        setLoading(false);
      }
    };

    loadVouchers();
  }, [daysFilter]);

  const handleRowClick = async (flight, rowKey) => {
    if (expandedRow === rowKey) {
      setExpandedRow(null);
      return;
    }

    setExpandedRow(rowKey);

    if (rowDetailData[rowKey]) return;

    try {
      setFetchingRow(rowKey);
      setErrorRow(null);

      let flightDate = new Date().toISOString().split('T')[0];
      if (flight.journeyDate && flight.journeyDate !== 'N/A') {
        const parsed = parseAnyDate(flight.journeyDate);
        if (parsed) {
          flightDate = parsed.toISOString().split('T')[0];
        }
      }

      const cleanFlightNum = String(flight.flightNumber).replace(/[^a-zA-Z0-9]/g, '');
      const result = await searchFlight(cleanFlightNum, flightDate);

      if (result && result.data && result.data.length > 0) {
        const cleanUpper = cleanFlightNum.toUpperCase();
        const bestMatch = result.data.find(f => {
          const iata = String(f.flight?.iata || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          const icao = String(f.flight?.icao || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          const num = String(f.flight?.number || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          return iata === cleanUpper || icao === cleanUpper || num === cleanUpper;
        }) || result.data.find(f => {
          const csIata = String(f.flight?.codeshared?.flight_iata || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          const csIcao = String(f.flight?.codeshared?.flight_icao || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          const csNum = String(f.flight?.codeshared?.flight_number || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          return csIata === cleanUpper || csIcao === cleanUpper || csNum === cleanUpper;
        }) || result.data[0];

        setRowDetailData((prev) => ({
          ...prev,
          [rowKey]: bestMatch
        }));
      } else {
        setErrorRow(rowKey);
      }
    } catch (err) {
      console.error('Error fetching live flight for row:', err);
      setErrorRow(rowKey);
    } finally {
      setFetchingRow(null);
    }
  };

  const calculateDaysToGo = (dateStr) => {
    const badge = getTimelineBadge(dateStr);
    return badge.text || 'Scheduled';
  };

  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 overflow-hidden relative mt-8">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 relative z-30 gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="w-44">
            <CustomSelect
              value={daysFilter}
              onChange={(val) => setDaysFilter(Number(val))}
              options={DAYS_FILTER_OPTIONS}
              icon={Filter}
              buttonClassName="!py-1.5 !px-3 !text-xs font-bold border-dashed border-slate-200 bg-slate-50 hover:bg-slate-100 rounded-xl !h-[36px]"
            />
          </div>

          <div className="hidden sm:block h-4 w-px bg-slate-200"></div>

          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-lg text-primary">
              <Plane size={14} />
            </div>
            Upcoming Flight Trips
          </h3>
        </div>
      </div>

      <div className="overflow-x-auto relative z-0">
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="flight-skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="py-1"
            >
              <TableSkeleton columns={flightColumns} rows={4} cardWrapper={false} />
            </motion.div>
          ) : (
            <motion.div
              key="flight-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {flightVouchers.length > 0 ? (
                <table className="w-full text-left border-separate border-spacing-y-0 min-w-[600px]">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      <th className="pb-4 pl-4">Guest & ID</th>
                      <th className="pb-2">Flight Details</th>
                      <th className="pb-2">Route & Time</th>
                      <th className="pb-2 text-right pr-4">Timeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flightVouchers.map((flight, idx) => (
                      <React.Fragment key={idx}>
                        <tr
                          onClick={() => handleRowClick(flight, `${flight.bookingId}-${idx}`)}
                          className={`group cursor-pointer transition-all duration-300 ${
                            expandedRow === `${flight.bookingId}-${idx}` ? 'bg-primary/5' : 'hover:bg-slate-50'
                          }`}
                        >
                          <td
                            className={`py-4 px-4 rounded-l-2xl border-y border-l border-slate-100 transition-colors ${
                              expandedRow === `${flight.bookingId}-${idx}` ? 'border-primary/20' : ''
                            }`}
                          >
                            <div className="flex flex-col">
                              <span className="font-bold text-slate-800 text-sm flex items-center gap-2">
                                {flight.guestName}
                                {expandedRow === `${flight.bookingId}-${idx}` ? (
                                  <ChevronUp size={14} className="text-primary" />
                                ) : (
                                  <ChevronDown
                                    size={14}
                                    className="text-slate-300 group-hover:text-primary transition-colors"
                                  />
                                )}
                              </span>
                              <span className="text-[10px] text-slate-400 font-mono tracking-tight uppercase">
                                {formatBookingId(flight.bookingId)}
                              </span>
                            </div>
                          </td>
                          <td
                            className={`py-4 border-y border-slate-100 transition-colors ${
                              expandedRow === `${flight.bookingId}-${idx}` ? 'border-primary/20' : ''
                            }`}
                          >
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1.5 text-xs text-slate-600 font-bold">
                                <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-md">
                                  #{flight.flightNumber}
                                </span>
                              </div>
                              <div className="text-[10px] text-slate-500 font-mono tracking-wider">
                                PNR: {flight.pnr}
                              </div>
                            </div>
                          </td>
                          <td
                            className={`py-4 border-y border-slate-100 transition-colors ${
                              expandedRow === `${flight.bookingId}-${idx}` ? 'border-primary/20' : ''
                            }`}
                          >
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1 text-xs text-slate-700 font-semibold">
                                <MapPin size={10} className="text-primary/60" />
                                <span>
                                  {flight.from} → {flight.to}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 text-[10px] text-slate-500">
                                <Clock size={10} />
                                <span>{flight.departureTime}</span>
                              </div>
                            </div>
                          </td>
                          <td
                            className={`py-4 px-4 rounded-r-2xl border-y border-r border-slate-100 transition-colors text-right ${
                              expandedRow === `${flight.bookingId}-${idx}` ? 'border-primary/20' : ''
                            }`}
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-xs font-bold text-slate-700">
                                {flight.journeyDate !== 'N/A' ? flight.journeyDate : flight.startDate}
                              </span>
                              <span
                                className={`inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                                  calculateDaysToGo(flight.journeyDate !== 'N/A' ? flight.journeyDate : flight.startDate) === 'Today'
                                    ? 'bg-red-50 text-red-600 border-red-100'
                                    : calculateDaysToGo(flight.journeyDate !== 'N/A' ? flight.journeyDate : flight.startDate) === 'Tomorrow'
                                    ? 'bg-amber-50 text-amber-600 border-amber-100'
                                    : 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                }`}
                              >
                                {calculateDaysToGo(flight.journeyDate !== 'N/A' ? flight.journeyDate : flight.startDate)}
                              </span>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Results Section */}
                        {expandedRow === `${flight.bookingId}-${idx}` && (
                          <tr>
                            <td colSpan="4" className="py-4 px-2">
                              <div className="bg-slate-50/50 rounded-3xl p-4 border border-slate-100 animate-fade-in">
                                {fetchingRow === `${flight.bookingId}-${idx}` ? (
                                  <div className="flex flex-col items-center justify-center py-10 gap-3">
                                    <Loader2 size={30} className="text-primary animate-spin" />
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                      Fetching Flight status...
                                    </p>
                                  </div>
                                ) : errorRow === `${flight.bookingId}-${idx}` ? (
                                  <div className="flex flex-col items-center justify-center py-10 gap-2 text-red-400">
                                    <AlertCircle size={30} />
                                    <p className="text-[10px] font-bold uppercase tracking-widest">
                                      Failed to fetch live status
                                    </p>
                                  </div>
                                ) : rowDetailData[`${flight.bookingId}-${idx}`] ? (
                                  <FlightTrackingResults
                                    flightData={rowDetailData[`${flight.bookingId}-${idx}`]}
                                  />
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyStateTable
                  title="No upcoming flight trips"
                  description={`When your vouchers include flight bookings with valid flight numbers, live flight tracking, gate details, and departure timelines for the next ${daysFilter} days will appear here automatically.`}
                  columns={flightColumns}
                  rows={flightGhostRows}
                  primaryAction={{
                    label: 'Create Voucher',
                    onClick: () => navigate('/create-voucher')
                  }}
                  renderRow={(row) => (
                    <div className={`hidden md:grid grid-cols-12 gap-4 items-center px-6 py-3.5 bg-white border-b border-slate-100 ${row.opacity || 'opacity-30'} select-none pointer-events-none`}>
                      <div className="col-span-3">
                        <p className="text-slate-800 text-sm font-bold">{row.guest}</p>
                        <p className="text-[10px] text-slate-400 font-mono tracking-tight uppercase">{row.bookingId}</p>
                      </div>
                      <div className="col-span-3 flex flex-col gap-1">
                        <span className="bg-[#067978]/10 text-[#067978] px-2 py-0.5 rounded-md text-xs font-bold w-fit">
                          #{row.flightNumber}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">PNR: {row.pnr}</span>
                      </div>
                      <div className="col-span-3 flex flex-col gap-0.5">
                        <div className="flex items-center gap-1 text-xs text-slate-700 font-semibold">
                          <MapPin size={10} className="text-[#067978]/60" />
                          <span>
                            {row.from} → {row.to}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-slate-500">
                          <Clock size={10} />
                          <span>{row.departureTime}</span>
                        </div>
                      </div>
                      <div className="col-span-3 text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${row.statusClass}`}>
                          {row.timeline}
                        </span>
                      </div>
                    </div>
                  )}
                  renderMobileRow={(row) => (
                    <div className={`bg-white border rounded-2xl p-3.5 border-slate-100 ${row.opacity || 'opacity-30'} select-none pointer-events-none`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-slate-800 text-sm font-bold">{row.guest}</h4>
                          <p className="text-[11px] text-slate-400 font-normal">
                            Flight #{row.flightNumber} · {row.from} → {row.to}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${row.statusClass}`}>
                          {row.timeline}
                        </span>
                      </div>
                    </div>
                  )}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default UpcomingFlightsTable;
